import React, { useMemo } from "react";
import {
   Checkbox,
   DateSelect,
   FlexList,
   Input,
   MultiSelect,
   NumberInput,
   Select,
   useI18nContext,
} from "@procore/core-react";
import { CustomFieldType } from "@laborchart-modules/common/dist/rethink/schemas/common/custom-field-type";
import { getAttachedDate, getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { dateClassifiers, filterClassifiers } from "@/react/shared/constants";
import type { Filter } from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/common";
import { FilterFieldType } from "@laborchart-modules/common/dist/rethink/schemas/generated-reports/enums/common";

export type ProjectCustomFieldDefinition = {
   id: string;
   name: string;
   type: string;
   values?: string[];
   sort_by?: string | null;
};

type ProjectCustomFieldFilterProps = {
   field: ProjectCustomFieldDefinition;
   value: Filter | null;
   onChange: (filter: Filter | null) => void;
};

const CF_TYPE_TO_FILTER_TYPE: Record<string, FilterFieldType> = {
   [CustomFieldType.BOOL]: FilterFieldType.BOOL,
   [CustomFieldType.CURRENCY]: FilterFieldType.CURRENCY,
   [CustomFieldType.DATE]: FilterFieldType.DATE,
   [CustomFieldType.MULTI_SELECT]: FilterFieldType.MULTI_SELECT,
   [CustomFieldType.NUMBER]: FilterFieldType.NUMBER,
   [CustomFieldType.SELECT]: FilterFieldType.SELECT,
   [CustomFieldType.TEXT]: FilterFieldType.TEXT,
};

function buildFilter(
   field: ProjectCustomFieldDefinition,
   value: unknown,
   classifier?: string,
): Filter {
   return {
      name: field.name,
      property: "custom_fields",
      custom_field_id: field.id,
      type: CF_TYPE_TO_FILTER_TYPE[field.type] ?? FilterFieldType.TEXT,
      value_sets: [
         {
            value: value as string,
            negation: false,
            ...(classifier
               ? { classifier: classifier as Filter["value_sets"][0]["classifier"] }
               : {}),
         },
      ],
   };
}

function BoolFilter({ field, value, onChange }: ProjectCustomFieldFilterProps) {
   const I18n = useI18nContext();
   const selectedValues = (value?.value_sets ?? []).map((vs) => vs.value as boolean);

   const handleToggle = (boolVal: boolean) => {
      const current = [...selectedValues];
      const idx = current.indexOf(boolVal);
      if (idx > -1) {
         current.splice(idx, 1);
      } else {
         current.push(boolVal);
      }

      if (current.length === 0) {
         onChange(null);
         return;
      }

      onChange({
         name: field.name,
         property: "custom_fields",
         custom_field_id: field.id,
         type: FilterFieldType.BOOL,
         value_sets: current.map((v) => ({ value: v, negation: false })),
      });
   };

   return (
      <div className="boardsFilterLabel">
         <h3 className="filterName">{field.name}</h3>
         <Checkbox checked={selectedValues.includes(true)} onChange={() => handleToggle(true)}>
            {I18n.t("views.company.workforce_planning.boolean_values.yes_value")}
         </Checkbox>
         <Checkbox checked={selectedValues.includes(false)} onChange={() => handleToggle(false)}>
            {I18n.t("views.company.workforce_planning.boolean_values.no_value")}
         </Checkbox>
      </div>
   );
}

function SelectFilter({ field, value, onChange }: ProjectCustomFieldFilterProps) {
   const options = useMemo(
      () => (field.values ?? []).map((v) => ({ key: v, name: v })),
      [field.values],
   );

   const selectedOptions = useMemo(() => {
      if (!value) return [];
      return value.value_sets.flatMap((vs) => {
         const val = vs.value as string | string[];
         return Array.isArray(val)
            ? val.map((v) => ({ key: v, name: v }))
            : [{ key: val, name: val }];
      });
   }, [value]);

   const handleChange = (selected: Array<{ key: string; name: string }>) => {
      if (selected.length === 0) {
         onChange(null);
         return;
      }

      const isMultiSelect = field.type === CustomFieldType.MULTI_SELECT;
      onChange({
         name: field.name,
         property: "custom_fields",
         custom_field_id: field.id,
         type: isMultiSelect ? FilterFieldType.MULTI_SELECT : FilterFieldType.SELECT,
         // Each multi-select value is wrapped in its own single-item array so
         // the backend OR's them (Postgres @> containment per value_set).
         // Grouping into one array would change the semantics to AND, breaking the expected behavior.
         value_sets: selected.map((s) => ({
            value: isMultiSelect ? [s.key] : s.key,
            negation: false,
         })),
      });
   };

   return (
      <div className="boardsFilterLabel">
         <h3 className="filterName">{field.name}</h3>
         <MultiSelect
            getId={(item) => item.key}
            getLabel={(item) => item.name}
            onChange={handleChange}
            options={options}
            value={selectedOptions}
            className="boardsFilterMultiselect"
         />
      </div>
   );
}

function NumberOrCurrencyFilter({ field, value, onChange }: ProjectCustomFieldFilterProps) {
   const I18n = useI18nContext();
   const currentValue = value?.value_sets[0]?.value as number | undefined;

   // Classifier is held in local state so the user can select it before
   // entering a value -- the parent Filter object is only built once both
   // a classifier and a value exist. The parent uses a key that changes
   // when the filter is cleared, which remounts this component and resets
   // the local state naturally.
   const [classifier, setClassifier] = React.useState<string | undefined>(
      value?.value_sets[0]?.classifier ?? undefined,
   );

   const classifierLabel = classifier
      ? I18n.t(filterClassifiers.find((c) => c.value === classifier)?.label ?? "")
      : undefined;

   const handleClassifierChange = (selection: any) => {
      const selected = filterClassifiers.find((x) => x.value === selection.item);
      if (!selected) return;
      setClassifier(selected.value);
      if (currentValue != null) {
         onChange(buildFilter(field, currentValue, selected.value));
      }
   };

   const handleValueChange = (val: { value: string; parsedNumber: number | null }) => {
      if (val.parsedNumber == null) {
         onChange(null);
         return;
      }
      onChange(buildFilter(field, val.parsedNumber, classifier));
   };

   return (
      <div className="boardsFilterLabel">
         <h3 className="filterName">{field.name}</h3>
         <FlexList>
            <Select
               label={classifierLabel}
               onSelect={handleClassifierChange}
               placeholder={I18n.t("views.company.workforce_planning.classifiers.placeholder")}
               style={{ width: "190px" }}
            >
               {filterClassifiers.map((c) => (
                  <Select.Option key={c.id} value={c.value} selected={c.value === classifier}>
                     {I18n.t(c.label)}
                  </Select.Option>
               ))}
            </Select>
            <NumberInput
               disabled={!classifier}
               onChange={handleValueChange}
               value={currentValue ?? null}
               placeholder={I18n.t("views.company.workforce_planning.filters.value_placeholder")}
            />
         </FlexList>
      </div>
   );
}

function TextFilter({ field, value, onChange }: ProjectCustomFieldFilterProps) {
   const I18n = useI18nContext();
   const currentValue = (value?.value_sets[0]?.value as string) ?? "";

   const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const text = event.target.value;
      if (!text) {
         onChange(null);
         return;
      }
      onChange(buildFilter(field, text));
   };

   return (
      <div className="boardsFilterLabel">
         <h3 className="filterName">{field.name}</h3>
         <Input
            value={currentValue}
            onChange={handleChange}
            placeholder={I18n.t("views.company.workforce_planning.filters.value_placeholder")}
            style={{ width: "100%" }}
         />
      </div>
   );
}

function DateFilter({ field, value, onChange }: ProjectCustomFieldFilterProps) {
   const I18n = useI18nContext();
   const currentValue = value?.value_sets[0]?.value as number | undefined;

   // Classifier is held in local state so the user can select it before
   // picking a date -- the parent Filter object is only built once both
   // a classifier and a date exist. The parent uses a key that changes
   // when the filter is cleared, which remounts this component and resets
   // the local state naturally.
   const [classifier, setClassifier] = React.useState<string | undefined>(
      value?.value_sets[0]?.classifier ?? undefined,
   );

   const classifierLabel = classifier
      ? I18n.t(dateClassifiers.find((c) => c.value === classifier)?.label ?? "")
      : undefined;

   const handleClassifierChange = (selection: any) => {
      const selected = dateClassifiers.find((x) => x.value === selection.item);
      if (!selected) return;
      setClassifier(selected.value);
      if (currentValue != null) {
         onChange(buildFilter(field, currentValue, selected.value));
      }
   };

   const handleDateChange = (date: Date | null) => {
      if (!date) {
         onChange(null);
         return;
      }
      const detachedDay = getDetachedDay(date);
      onChange(buildFilter(field, detachedDay, classifier));
   };

   return (
      <div className="boardsFilterLabel">
         <h3 className="filterName">{field.name}</h3>
         <FlexList>
            <Select
               label={classifierLabel}
               onSelect={handleClassifierChange}
               placeholder={I18n.t("views.company.workforce_planning.classifiers.placeholder")}
               style={{ width: "190px" }}
            >
               {dateClassifiers.map((c) => (
                  <Select.Option key={c.id} value={c.value} selected={c.value === classifier}>
                     {I18n.t(c.label)}
                  </Select.Option>
               ))}
            </Select>
            <DateSelect
               disabled={!classifier}
               value={currentValue ? getAttachedDate(currentValue) : undefined}
               onChange={handleDateChange}
            />
         </FlexList>
      </div>
   );
}

export function ProjectCustomFieldFilter(props: ProjectCustomFieldFilterProps) {
   switch (props.field.type) {
      case CustomFieldType.BOOL:
         return <BoolFilter {...props} />;
      case CustomFieldType.SELECT:
      case CustomFieldType.MULTI_SELECT:
         return <SelectFilter {...props} />;
      case CustomFieldType.NUMBER:
      case CustomFieldType.CURRENCY:
         return <NumberOrCurrencyFilter {...props} />;
      case CustomFieldType.TEXT:
         return <TextFilter {...props} />;
      case CustomFieldType.DATE:
         return <DateFilter {...props} />;
      default:
         return null;
   }
}
